import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Instagram, Trash2, PlusCircle, AlertTriangle } from 'lucide-react';
import * as api from '../../api/client';
import { AppError } from '../../api/request';
import { useConfirm } from '../../context/ConfirmContext';
import { useInstagramAccounts } from '../../context/InstagramAccountContext';
import PageHeader from '../components/PageHeader';

const OAUTH_ERROR_KEYS: Record<string, string> = {
  oauth_denied: 'accounts.oauthError.denied',
  no_instagram_business: 'accounts.oauthError.noBusiness',
  account_limit_reached: 'accounts.oauthError.limitReached',
};

export default function AccountsPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { accounts, refresh, loading } = useInstagramAccounts();
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const connected = params.get('instagram_connected');
    const errCode = params.get('instagram_error');
    if (connected) {
      refresh();
      setParams({}, { replace: true });
    } else if (errCode) {
      setError(t(OAUTH_ERROR_KEYS[errCode] ?? 'accounts.oauthError.generic'));
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function connect() {
    setError('');
    setConnecting(true);
    try {
      const url = await api.getInstagramOAuthURL();
      window.location.href = url;
    } catch (err) {
      setError(err instanceof AppError ? err.message : t('accounts.connectError'));
      setConnecting(false);
    }
  }

  async function disconnect(id: string) {
    const ok = await confirm({
      title: t('accounts.disconnectTitle'),
      message: t('accounts.disconnectConfirm'),
      confirmLabel: t('accounts.disconnectTitle'),
      variant: 'danger',
    });
    if (!ok) return;
    await api.disconnectInstagramAccount(id);
    refresh();
  }

  return (
    <div>
      <PageHeader
        icon={Instagram}
        title={t('accounts.title')}
        action={
          <button onClick={connect} disabled={connecting} className="btn-gradient">
            <PlusCircle className="h-4 w-4" /> {connecting ? t('common.redirecting') : t('accounts.connectButton')}
          </button>
        }
      />

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 text-red-700 px-4 py-3 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-400 dark:text-lc-faint text-sm">{t('common.loading')}</p>
      ) : accounts.length === 0 ? (
        <div className="card border-dashed p-10 text-center">
          <span className="icon-chip mx-auto">
            <Instagram className="h-5 w-5" />
          </span>
          <p className="mt-4 font-semibold text-gray-900 dark:text-lc-text">{t('accounts.emptyTitle')}</p>
          <p className="mt-1 text-sm text-gray-400 dark:text-lc-faint">
            {t('accounts.emptySubtitle')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {accounts.map((a) => (
            <div key={a.id} className="card p-5 flex items-center justify-between hover:shadow-md">
              <div className="flex items-center gap-3">
                {a.profilePictureUrl ? (
                  <img
                    src={a.profilePictureUrl}
                    alt={a.igUsername ? `@${a.igUsername}` : a.pageName || ''}
                    referrerPolicy="no-referrer"
                    className="h-11 w-11 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-purple via-brand-pink to-brand-orange text-sm font-bold text-white">
                    {(a.igUsername || a.pageName || '?').charAt(0).toUpperCase()}
                  </span>
                )}
                <div>
                  <p className="font-semibold text-gray-900 dark:text-lc-text">@{a.igUsername || t('accounts.noUsername')}</p>
                  <p className="text-sm text-gray-400 dark:text-lc-faint">{a.pageName}</p>
                  <span className={`badge mt-2 ${a.tokenStatus === 'valid' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {a.tokenStatus === 'valid' ? t('accounts.tokenValid') : t('accounts.tokenExpired')}
                  </span>
                </div>
              </div>
              <button onClick={() => disconnect(a.id)} className="text-gray-400 dark:text-lc-faint hover:text-red-600" title={t('accounts.disconnectTitle')}>
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
